/**
 * Calendar arithmetic for the events screen. Days are Warsaw wall-clock days ("YYYY-MM-DD");
 * day labels are added and compared as plain calendar dates (UTC arithmetic on the label, never the browser's zone).
 */
import { addDays, CONFIRMED_STATUSES, EVENT_STATUSES, isClosed, runsOf, warsawInstant, warsawParts, type EventView } from './labels';

export { runsOf };

export type CalendarView = 'week' | 'month' | 'year' | 'list';
/** An event as the list API sends it: every field but the id may be hidden from the role (rule 7a). */
export type CalendarEvent = Pick<EventView, 'id'> & Partial<Pick<EventView, 'name' | 'startsAt' | 'endsAt' | 'segments' | 'status' | 'kind' | 'needsQuote' | 'settlement' | 'location' | 'contractorId'>> & {
  /** Names of the crew (without those who declined), when the role may see crews (F18). */
  crew?: string[];
  /** „Dostępność → Ludzie”: someone withdrew their availability and the place is empty („nieobsadzone”). */
  unstaffed?: boolean;
  /** Owner 2026-10-23 20:49: the client's colour key (`withClientColors`); absent: none, or the client is not seen. */
  clientColor?: string | undefined;
};

/**
 * The client's colour on each event that has one: only when the event came with its `contractorId` (a role that may not
 * see the client gets no id, so no colour either — the colour never tells whose event it is) and the client has a colour.
 */
export function withClientColors<T extends CalendarEvent>(events: T[], colors: ReadonlyMap<string, string>): T[] {
  if (colors.size === 0) return events;
  return events.map((e) => {
    const color = e.contractorId ? colors.get(e.contractorId) : undefined;
    return color ? { ...e, clientColor: color } : e;
  });
}
/**
 * An event the role may see the dates of: only these can be placed on a calendar grid. `piece`: one term of an event
 * of several terms (owner 2026-10-21 00:44, `eventPieces`): its number, how many there are and how many days after the
 * event's first day it starts (a dragged piece moves the event by as many days as the piece was moved).
 */
export type DatedEvent = CalendarEvent & { startsAt: string; endsAt: string; piece?: { index: number; count: number; offsetDays: number } };

export function withDates(events: CalendarEvent[]): DatedEvent[] {
  return events.filter((e): e is DatedEvent => typeof e.startsAt === 'string' && typeof e.endsAt === 'string');
}

/** The name, or a neutral word when the role may not see it. */
export function eventName(e: CalendarEvent): string {
  return e.name || 'Wydarzenie';
}

/** What a bar or row says to a screen reader beyond the name: the status and the quote flag, when visible. */
export function describeEvent(e: CalendarEvent): string {
  return [e.kind === 'wypozyczenie' ? 'wypożyczenie' : '', e.status ? EVENT_STATUSES[e.status] : '', e.needsQuote ? 'potrzebna wycena' : ''].filter(Boolean).join(', ');
}

/** "Szczegóły: <name> (<status, flag>)", without empty brackets. */
export function detailsLabel(e: CalendarEvent): string {
  const extra = describeEvent(e);
  const crew = e.crew && e.crew.length > 0 ? `, obsada: ${e.crew.join(', ')}` : '';
  return `Szczegóły: ${eventName(e)}${extra ? ` (${extra})` : ''}${crew}${e.unstaffed ? ', nieobsadzone' : ''}`;
}

export const WEEKDAYS_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'] as const;
export const MONTHS = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'] as const;
export const MONTHS_GENITIVE = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'] as const;

const parts = (day: string) => day.split('-').map(Number) as [number, number, number];
const label = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(day: string): number {
  const [y, m, d] = parts(day);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function startOfWeek(day: string): string {
  return addDays(day, -weekdayIndex(day));
}

export function startOfMonth(day: string): string {
  const [y, m] = parts(day);
  return label(y, m, 1);
}

export function todayInWarsaw(now: Date = new Date()): string {
  return warsawParts(now.toISOString()).day;
}

/** Weeks (Monday..Sunday) covering the month of `day`: 4 to 6 rows of 7 days. */
export function monthGrid(day: string): string[][] {
  const [y, m] = parts(day);
  const last = label(y, m + 1, 0);
  const weeks: string[][] = [];
  for (let start = startOfWeek(label(y, m, 1)); start <= last; start = addDays(start, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => addDays(start, i)));
  }
  return weeks;
}

/** First and last Warsaw day the event touches. The end is exclusive: an end at midnight ends the day before. */
export function eventDays(e: Pick<DatedEvent, 'startsAt' | 'endsAt'>): { first: string; last: string } {
  const s = warsawParts(e.startsAt);
  const end = warsawParts(e.endsAt);
  let last = end.time === '00:00' ? addDays(end.day, -1) : end.day;
  if (last < s.day) last = s.day;
  return { first: s.day, last };
}

/**
 * The calendar draws an event of several terms as its terms (owner 2026-10-21 00:44: free in between): each term is
 * a piece with the event's id and the term's dates. An event of one term stays as it is.
 */
export function eventPieces(events: DatedEvent[]): DatedEvent[] {
  return events.flatMap((e) => {
    if (!e.segments || e.segments.length < 2) return [e];
    const first = eventDays(e).first;
    return e.segments.map((t, index) => ({ ...e, startsAt: t.startsAt, endsAt: t.endsAt, piece: { index, count: e.segments!.length, offsetDays: dayDiff(eventDays(t).first, first) } }));
  });
}

/** Events once each (a list of pieces of a day may hold two terms of the same event). */
export function uniqueEvents<T extends { id: string }>(events: T[]): T[] {
  const seen = new Set<string>();
  return events.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
}

export type WeekSegment = { event: DatedEvent; startCol: number; span: number; lane: number; continuesBefore: boolean; continuesAfter: boolean };

/** Bars for one week row: each event clipped to the week, stacked into the lowest free lane (longer events first). */
export function layoutWeek(week: string[], events: DatedEvent[]): WeekSegment[] {
  const first = week[0]!;
  const last = week[week.length - 1]!;
  const items = events
    .map((event) => ({ event, ...eventDays(event) }))
    .filter((x) => x.first <= last && x.last >= first)
    .sort((a, b) => (a.first !== b.first ? (a.first < b.first ? -1 : 1) : a.last !== b.last ? (a.last > b.last ? -1 : 1) : a.event.startsAt.localeCompare(b.event.startsAt)));
  const laneEnds: string[] = []; // last day taken in each lane
  return items.map(({ event, first: f, last: l }) => {
    const from = f < first ? first : f;
    const to = l > last ? last : l;
    let lane = laneEnds.findIndex((end) => end < from);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = to;
    return { event, startCol: week.indexOf(from), span: week.indexOf(to) - week.indexOf(from) + 1, lane, continuesBefore: f < first, continuesAfter: l > last };
  });
}

/**
 * Owner 2026-10-29 16:02 (the month like Apple Calendar): „wszystkie wydarzenia są kafelkami … wszystkie wypożyczenia
 * są kropkami” — in the month grid an event is a tile (a bar across its days), a rental a dot row on each of its days.
 * An event whose kind the role may not see stays a tile.
 */
export function monthKind(e: Pick<CalendarEvent, 'kind'>): 'tile' | 'dot' {
  return e.kind === 'wypozyczenie' ? 'dot' : 'tile';
}

/**
 * A rental's dot on one day of the week: `first` — the first day of the rental (or of its term), where its time shows;
 * `offsetDays` — days from the event's first day to this one (a dragged dot moves the rental so this day lands on the drop).
 */
export type RentalDay = { event: DatedEvent; first: boolean; offsetDays: number };

/** The rentals' dots by day of `week` (owner 2026-10-29: a rental of several days shows its dot on every day of it). */
export function rentalDays(week: readonly string[], rentals: DatedEvent[]): Map<string, RentalDay[]> {
  const out = new Map<string, RentalDay[]>();
  for (const day of week) {
    for (const event of rentals) {
      const d = eventDays(event);
      if (d.first > day || d.last < day) continue;
      out.set(day, [...(out.get(day) ?? []), { event, first: day === d.first, offsetDays: (event.piece?.offsetDays ?? 0) + dayDiff(day, d.first) }]);
    }
  }
  return out;
}

/** The dot rows of a day in time order: those without a time (the whole day) first, then by time, then by name. */
export function sortDots<T extends { time: string; name: string }>(dots: readonly T[]): T[] {
  return [...dots].sort((a, b) => (a.time !== b.time ? (a.time === '' ? -1 : b.time === '' ? 1 : a.time.localeCompare(b.time)) : a.name.localeCompare(b.name, 'pl')));
}

export type TimedPlacement = { event: DatedEvent; day: string; startMin: number; endMin: number };

/** Week view: events over several days or without hours go to the top band; a timed event within one day goes to the hour grid. */
export function splitWeekEvents(events: DatedEvent[]): { allDay: DatedEvent[]; timed: TimedPlacement[] } {
  const allDay: DatedEvent[] = [];
  const timed: TimedPlacement[] = [];
  for (const event of events) {
    const s = warsawParts(event.startsAt);
    const e = warsawParts(event.endsAt);
    const { first, last } = eventDays(event);
    const wholeDays = s.time === '00:00' && e.time === '00:00';
    // an evening event ending exactly at midnight is still a one-day event with hours (ends at 24:00)
    const endsAtMidnight = e.time === '00:00' && e.day === addDays(s.day, 1);
    if (first !== last || wholeDays || (e.day !== s.day && !endsAtMidnight)) {
      allDay.push(event);
    } else {
      const minutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
      timed.push({ event, day: first, startMin: minutes(s.time), endMin: endsAtMidnight ? 24 * 60 : minutes(e.time) });
    }
  }
  return { allDay, timed };
}

/** Hours of the week grid: 6:00–24:00, started earlier (never clamped) when a timed event begins before 6:00. */
export function weekHours(timed: TimedPlacement[]): { from: number; to: number } {
  const from = Math.min(6, ...timed.map((t) => Math.floor(t.startMin / 60)));
  const to = Math.max(24, ...timed.map((t) => Math.ceil(t.endMin / 60)));
  return { from, to };
}

/** Said when the API cut the list at 200: points at what the screen offers right now. */
export function truncatedNote(shown: CalendarView, searching: boolean): string {
  if (searching) return 'Pokazano pierwszych 200 wyników, od najpóźniejszego.';
  if (shown === 'list') return 'Pokazano 200 najpóźniejszych wydarzeń. Starsze znajdziesz, wpisując nazwę lub miejsce albo wybierając status.';
  return 'W tym okresie jest więcej niż 200 wydarzeń; pokazano pierwsze 200. Wybierz krótszy widok albo status.';
}

export type DayLoad = { count: number; needsQuote: boolean };

/** Year view: how many live events cover each day, and whether any of them still needs a quote. */
export function busyDays(events: DatedEvent[]): Map<string, DayLoad> {
  const map = new Map<string, DayLoad>();
  const counted = new Set<string>();
  for (const event of events) {
    if (isClosed(event.status)) continue;
    const { first, last } = eventDays(event);
    for (let day = first; day <= last; day = addDays(day, 1)) {
      // two terms of one event on the same day count once
      const key = `${event.id}|${day}`;
      if (counted.has(key)) continue;
      counted.add(key);
      const cur = map.get(day) ?? { count: 0, needsQuote: false };
      map.set(day, { count: cur.count + 1, needsQuote: cur.needsQuote || event.needsQuote === true });
    }
  }
  return map;
}

/** Days on screen, inclusive, as the API's `from` / `to`; the list is not limited to dates. */
export function rangeFor(view: CalendarView, anchor: string): { from: string; to: string } | null {
  if (view === 'week') return { from: startOfWeek(anchor), to: addDays(startOfWeek(anchor), 6) };
  if (view === 'month') {
    const grid = monthGrid(anchor);
    return { from: grid[0]![0]!, to: grid.at(-1)!.at(-1)! };
  }
  if (view === 'year') {
    const y = parts(anchor)[0];
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  return null;
}

export function shiftAnchor(view: CalendarView, anchor: string, dir: 1 | -1): string {
  const [y, m] = parts(anchor);
  if (view === 'week') return addDays(anchor, 7 * dir);
  if (view === 'month') return label(y, m + dir, 1);
  if (view === 'year') return label(y + dir, 1, 1);
  return anchor;
}

export function heading(view: CalendarView, anchor: string): string {
  const [y, m] = parts(anchor);
  if (view === 'year') return String(y);
  if (view === 'week') {
    const a = parts(startOfWeek(anchor));
    const b = parts(addDays(startOfWeek(anchor), 6));
    if (a[0] !== b[0]) return `${a[2]} ${MONTHS_GENITIVE[a[1] - 1]} ${a[0]} – ${b[2]} ${MONTHS_GENITIVE[b[1] - 1]} ${b[0]}`;
    if (a[1] !== b[1]) return `${a[2]} ${MONTHS_GENITIVE[a[1] - 1]} – ${b[2]} ${MONTHS_GENITIVE[b[1] - 1]} ${b[0]}`;
    return `${a[2]}–${b[2]} ${MONTHS_GENITIVE[b[1] - 1]} ${b[0]}`;
  }
  return `${MONTHS[m - 1]} ${y}`;
}

/** Day of the month of a "YYYY-MM-DD" label. */
export function dayNumber(day: string): number {
  return parts(day)[2];
}

/** `GET /api/events` with the search, status and visible days. Uses `toString()`: `URLSearchParams.size` is missing on Safari < 17. */
export function eventsListUrl({ q, status, range }: { q: string; status: string; range: { from: string; to: string } | null }): string {
  const p = new URLSearchParams();
  if (q) p.set('q', q);
  if (status) p.set('status', status);
  if (range) {
    p.set('from', range.from);
    p.set('to', range.to);
  }
  const qs = p.toString();
  return `/api/events${qs ? `?${qs}` : ''}`;
}

// ---------- P6: filters of the calendar and moving events by days ----------

/** What the calendar shows: "Tylko potwierdzone" and "Pokaż odrzucone/rezygnacje" (both not remembered), the status picked. */
/**
 * `showRentals` (F28): rentals of gear to other companies, off unless switched on (not remembered); `searching`: a typed
 * search finds them anyway (as a chosen closed status shows closed events).
 */
export type CalendarFilter = { onlyConfirmed: boolean; showClosed: boolean; status: string; showRentals?: boolean; searching?: boolean };

/**
 * Cancelled and resigned events stay hidden unless "Pokaż odrzucone/rezygnacje" is on or such a status is picked
 * in the status list; "Tylko potwierdzone" keeps confirmed and later statuses (an unknown status — hidden from the
 * role — is not confirmed, but is not closed either).
 */
export function filterCalendar<T extends CalendarEvent>(events: T[], f: CalendarFilter): T[] {
  const showClosed = f.showClosed || isClosed(f.status);
  return events.filter((e) => {
    if (e.kind === 'wypozyczenie' && !f.showRentals && !f.searching) return false;
    if (isClosed(e.status)) return showClosed;
    if (f.onlyConfirmed) return e.status !== undefined && CONFIRMED_STATUSES.includes(e.status);
    return true;
  });
}

/** Whole days from `b` to `a` ("YYYY-MM-DD" labels). */
export function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000);
}

/** The event moved to start on `firstDay`: the same Warsaw hours and the same number of days (as the server's copy). */
export function shiftToDay(e: Pick<DatedEvent, 'startsAt' | 'endsAt'>, firstDay: string): { startsAt: string; endsAt: string } {
  const s = warsawParts(e.startsAt);
  const end = warsawParts(e.endsAt);
  const delta = dayDiff(firstDay, s.day);
  return { startsAt: warsawInstant(firstDay, s.time), endsAt: warsawInstant(addDays(end.day, delta), end.time) };
}

/**
 * Owner 2026-10-24 00:46 („jak przeciągam wydarzenie … pytaj przed upuszczeniem”): the event as it would be after a
 * move to `firstDay` — its bar waits there while the question is open. Each term moves by the same days (as the server does).
 */
export function movedEvent<E extends DatedEvent>(e: E, firstDay: string): E {
  const delta = dayDiff(firstDay, eventDays(e).first);
  const segments = e.segments?.map((t) => shiftToDay(t, addDays(eventDays(t).first, delta)));
  return { ...e, ...shiftToDay(e, firstDay), ...(segments ? { segments } : {}) };
}

/**
 * Owner 21.10 00:44: the days picked in the calendar (a click, the arrows, Shift / Cmd) go into "Nowe wydarzenie",
 * "Nowe wypożyczenie" and "Nowe zadanie firmowe": one day as `dzien=`, several (an event of several terms) as
 * `dni=` (sorted, by commas).
 */
export function newOnDayUrl(path: string, days: string | readonly string[] | null): string {
  const list = days === null ? [] : typeof days === 'string' ? [days] : [...days].sort();
  if (list.length === 0) return path;
  const sep = path.includes('?') ? '&' : '?';
  return list.length === 1 ? `${path}${sep}dzien=${list[0]}` : `${path}${sep}dni=${list.join(',')}`;
}

// ---------- picking days: Shift = a run of days, Cmd / Ctrl = single days (owner 2026-10-21 00:44) ----------

/**
 * The picked days (sorted), the day clicked last (`anchor`: ⌘V pastes there, Shift runs from it) and the days
 * picked before the run Shift is making from it (`base`).
 */
export type DaySelection = { days: string[]; anchor: string | null; base: string[] };
export const EMPTY_SELECTION: DaySelection = { days: [], anchor: null, base: [] };

const sortedUnique = (days: readonly string[]) => [...new Set(days)].sort();

/** After a click on `day`: plain — just that day; `range` (Shift) — the run from the anchor; `toggle` (Cmd / Ctrl) — that day on or off. */
export function nextSelection(cur: DaySelection, day: string, mods: { range?: boolean; toggle?: boolean }): DaySelection {
  if (mods.range && cur.anchor) {
    const [from, to] = cur.anchor <= day ? [cur.anchor, day] : [day, cur.anchor];
    const run: string[] = [];
    for (let d = from; d <= to && run.length < 400; d = addDays(d, 1)) run.push(d);
    return { days: sortedUnique([...cur.base, ...run]), anchor: cur.anchor, base: cur.base };
  }
  // Cmd / Ctrl, or Shift with no active day: that day on or off; a day taken off is not the active day (⌘V, arrows)
  if (mods.toggle || mods.range) {
    if (cur.days.includes(day)) {
      const days = cur.days.filter((d) => d !== day);
      return { days, anchor: null, base: days };
    }
    const days = sortedUnique([...cur.days, day]);
    return { days, anchor: day, base: days.filter((d) => d !== day) };
  }
  return { days: [day], anchor: day, base: [] };
}

/**
 * Owner 2026-10-25 13:12: one mark in the calendar at a time. An event clicked on `day` drops the days picked before
 * (its day is marked by the page); a pick of just that day (the year view picks it with the same click) stays.
 */
export function pickedAfterOpen(cur: DaySelection, day: string): DaySelection {
  return cur.days.every((d) => d === day) ? cur : EMPTY_SELECTION;
}

/** "terminy" / "terminów" after a number (2–4 vs the rest). */
export function termsWord(n: number): string {
  return n === 1 ? 'termin' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'terminy' : 'terminów';
}

/** "Zaznaczone: 3 dni (2 terminy)" over the calendar. */
export function selectionSummary(days: readonly string[]): string {
  const n = sortedUnique(days).length;
  const runs = runsOf(days).length;
  return `Zaznaczone: ${n} ${n === 1 ? 'dzień' : 'dni'}${runs > 1 ? ` (${runs} ${termsWord(runs)})` : ''}`;
}

/** `dni=YYYY-MM-DD,…` of an address (owner 2026-10-21 00:44): real days only, sorted, without repeats. */
export function daysParam(value: string | null): string[] {
  return [...new Set((value ?? '').split(',').filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`)) && new Date(`${d}T00:00:00Z`).toISOString().startsWith(d)))].sort();
}

/**
 * The dates in the card's title and the bar's tooltip (owner 2026-10-22 01:04: "tytuł musi zawierać datę"): Warsaw
 * days as the calendar draws them — "14.11.2026", "14–15.11.2026", "30.11–2.12.2026", "31.12.2026–1.01.2027";
 * several terms compactly with the year once: "3–4.11, 7.11.2026". '' without dates.
 */
export function titleDates(e: { startsAt?: string | undefined; endsAt?: string | undefined; segments?: readonly { startsAt: string; endsAt: string }[] | undefined }): string {
  const terms = e.segments && e.segments.length > 1 ? e.segments : e.startsAt && e.endsAt ? [{ startsAt: e.startsAt, endsAt: e.endsAt }] : [];
  if (terms.length === 0) return '';
  const days = terms.map((t) => eventDays(t));
  const years = new Set(days.flatMap((d) => [d.first.slice(0, 4), d.last.slice(0, 4)]));
  const dm = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}`;
  const range = (d: { first: string; last: string }, withYear: boolean): string => {
    const y = (day: string) => (withYear ? `.${day.slice(0, 4)}` : '');
    if (d.first === d.last) return `${dm(d.first)}${y(d.first)}`;
    if (d.first.slice(0, 4) !== d.last.slice(0, 4)) return `${dm(d.first)}.${d.first.slice(0, 4)}–${dm(d.last)}.${d.last.slice(0, 4)}`;
    const start = d.first.slice(0, 7) === d.last.slice(0, 7) ? String(Number(d.first.slice(8, 10))) : dm(d.first);
    return `${start}–${dm(d.last)}${y(d.last)}`;
  };
  if (years.size > 1) return days.map((d) => range(d, true)).join(', ');
  return days.map((d, i) => range(d, i === days.length - 1)).join(', ');
}
